/**
 * MilestoneCaptureZone (§1.6 s127 Build 1) — the clay-accented date-capture control on the
 * wait-state tasks (receive-certificate-appointment → certificate_issued;
 * receive-notices-assessment → noa_received). Recording the date writes the estate_events
 * milestone, which re-anchors downstream deadlines (EIR 180 days after the certificate;
 * TX19 phrasing on the notices). Re-entering the date UPDATES it (executors correct typos).
 *
 * Data comes from the drawer's /bindings query (`milestone`); saving invalidates that query
 * plus the task list so the Due column flips from placeholder to a concrete date live.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { CLAY, WARM } from "@shared/brand";

const CLAY_BG = "#F7EFE8";

export interface DrawerMilestone {
  eventKey: string;
  capturedAt: string | null; // ISO, or null when not yet recorded
}

const COPY: Record<string, { label: string; prompt: string }> = {
  certificate_issued: {
    label: "Certificate of Appointment",
    prompt: "When did the court issue the Certificate of Appointment?",
  },
  noa_received: {
    label: "Notices of Assessment",
    prompt: "When did the last Notice of Assessment arrive?",
  },
};

export function MilestoneCaptureZone({
  clientId,
  taskKey,
  milestone,
}: {
  clientId: string;
  taskKey: string;
  milestone: DrawerMilestone;
}) {
  const qc = useQueryClient();
  const captured = milestone.capturedAt ? milestone.capturedAt.slice(0, 10) : "";
  const [value, setValue] = useState<string>(captured);
  const [editing, setEditing] = useState<boolean>(!captured);

  const copy = COPY[milestone.eventKey] ?? { label: "date", prompt: "Record the date" };

  const save = useMutation({
    mutationFn: async (occurredAt: string) =>
      (await apiRequest("POST", `/api/executor/${clientId}/tasks/${taskKey}/milestone`, {
        eventKey: milestone.eventKey,
        occurredAt,
      })).json(),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: [`/api/executor/${clientId}/tasks/${taskKey}/bindings`] });
      qc.invalidateQueries({ queryKey: [`/api/executor/${clientId}/tasks`] });
    },
  });

  // Format in UTC: the milestone date is stored as UTC-midnight, so a local-timezone render
  // west of UTC would show the prior day (the resolver's date math is already UTC-safe).
  const formatted = milestone.capturedAt
    ? new Date(milestone.capturedAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" })
    : null;

  return (
    <div
      className="mt-4 rounded-md p-3"
      style={{ backgroundColor: CLAY_BG, border: `1px solid ${WARM.stone}` }}
      data-testid={`milestone-zone-${milestone.eventKey}`}
    >
      <div className="flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: CLAY[600] }}>
        <CalendarCheck className="h-3.5 w-3.5" /> {copy.label}
      </div>

      {formatted && !editing ? (
        <div className="mt-1.5 flex items-center gap-2 text-[13px]" style={{ color: WARM.charcoal }}>
          <span data-testid={`milestone-recorded-${milestone.eventKey}`}>Recorded: {formatted}</span>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[12px] underline"
            style={{ color: WARM.taupe }}
            data-testid={`milestone-edit-${milestone.eventKey}`}
          >
            Edit
          </button>
        </div>
      ) : (
        <>
          <p className="mt-1 text-[13px]" style={{ color: WARM.taupe }}>{copy.prompt}</p>
          <div className="mt-2 flex items-center gap-2">
            <input
              type="date"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="rounded-md px-2 py-1.5 text-[13px] outline-none"
              style={{ border: `1px solid ${WARM.stone}`, color: WARM.charcoal }}
              data-testid={`milestone-date-input-${milestone.eventKey}`}
            />
            <button
              type="button"
              onClick={() => value && save.mutate(value)}
              disabled={!value || save.isPending}
              className="rounded-md px-3 py-1.5 text-[13px] font-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: CLAY[600] }}
              data-testid={`milestone-save-${milestone.eventKey}`}
            >
              {save.isPending ? "Saving…" : "Record date"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
